import { Box, ButtonDropdownProps, Link, SpaceBetween } from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import { eCacheKeys, generatePhoneToolUrl, generateProfilePictureUrl, PHONE_TOOL_URL } from 'src/constants/AppConstants';
import { getEmployeeByLogin } from 'src/api/papi-service';
import { ProfileInfo, mapEmployeeToProfileInfo, BADGE_SHADOW_MAP } from 'src/utilities/profile-utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContextProvider';
import { RecentsService } from 'src/services/recents/RecentsService';
import { RecentEntityType } from 'src/services/recents/RecentsConstants';
import 'src/styles/dfp-home-page/dfp-profile-component.scss';

const DFPProfileHeader: React.FC<{ onCancel: () => void; onOpenPreferences: () => void }> = (props) => {
  const { Alias: userAlias, DisplayName: fullName } = useAuth();
  const navigate = useNavigate();
  const nameList = fullName.split(',').reverse();
  const [profileInfo, setProfileInfo] = useState<ProfileInfo>({});
  const onCancel = props.onCancel;
  const onOpenPreferences = props.onOpenPreferences;

  // Open phonetool profile in new tab
  const handleNameClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(`${generatePhoneToolUrl(userAlias)}`, '_blank', 'noopener,noreferrer');
  };

  // Read profile info from sessionStorage (pre-warmed by DFPTopNavigation on page load).
  // Falls back to a direct PAPI fetch on cache miss (e.g. race condition or pre-warm failure).
  useEffect(() => {
    let cancelled = false;

    // Fallback: fetch directly if cache is empty
    const fetchProfileInfo = async () => {
      try {
        const response = await getEmployeeByLogin(userAlias, 'job');
        if (cancelled || !response.employee) return;

        const info = mapEmployeeToProfileInfo(response.employee);
        setProfileInfo(info);
      } catch {
        // Silently fail — profile info is supplementary
      }
    };

    fetchProfileInfo();
    return () => {
      cancelled = true;
    };
  }, [userAlias]);

  const locationParts = [profileInfo.city, profileInfo.buildingCode].filter(Boolean);
  const locationText = locationParts.length > 0 ? locationParts.join(', ') : undefined;
  const titleLevelText = [profileInfo.jobTitle, profileInfo.jobLevel ? `L${profileInfo.jobLevel}` : undefined].filter(Boolean).join(' · ');

  return (
    <div className="dfp-profile-header">
      <img
        src={generateProfilePictureUrl(userAlias)}
        alt={fullName || userAlias}
        className="dfp-profile-header__avatar"
        style={
          profileInfo.badgeColor && BADGE_SHADOW_MAP[profileInfo.badgeColor] ? { boxShadow: BADGE_SHADOW_MAP[profileInfo.badgeColor] } : undefined
        }
      />
      <div className="dfp-profile-header__info">
        <div className="dfp-profile-header__name-row">
          <div className="dfp-profile-header__name">{nameList[0] + ' ' + nameList[1]}</div>
          <div className="dfp-profile-header__alias">
            <Link
              onFollow={(e) => {
                e.preventDefault();
                handleNameClick(e as unknown as React.MouseEvent);
              }}
              href={PHONE_TOOL_URL + userAlias}
            >
              {userAlias}@
            </Link>
          </div>
        </div>
        <>
          {titleLevelText && <div className="dfp-profile-header__title">{titleLevelText}</div>}
          {locationText && <div className="dfp-profile-header__location">{locationText}</div>}
        </>
      </div>
      <div className="dfp-profile-header__actions">
        <Link
          href="#"
          onFollow={(e) => {
            e.preventDefault();
            onCancel();
            onOpenPreferences();
          }}
        >
          {'Preferences'}
        </Link>
        <Link
          href="/admin"
          onFollow={(e) => {
            e.preventDefault();
            navigate('/admin');
            onCancel();
          }}
        >
          {'Admin Console'}
        </Link>
      </div>
    </div>
  );
};

export const DFPProfileComponent = (props: {
  profile_items: ReadonlyArray<ButtonDropdownProps.ItemOrGroup>;
  onCancel: () => void;
  onOpenPreferences: () => void;
}) => {
  const { CognitoSub, Alias } = useAuth();

  return (
    <SpaceBetween size="s" direction="vertical">
      <DFPProfileHeader onCancel={props.onCancel} onOpenPreferences={props.onOpenPreferences} />
      <div className="dfp-profile-links">
        {props.profile_items.map((group) => {
          const itemGroup = group as ButtonDropdownProps.ItemGroup;
          return (
            <div key={group.id} className="dfp-profile-links__group">
              <div className="dfp-profile-links__main-btn">{group.text}</div>
              {itemGroup.items?.map((subItem) => {
                const item = subItem as ButtonDropdownProps.Item;
                return (
                  <a
                    key={item.id}
                    href={item.href || '#'}
                    className="dfp-profile-links__sub-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      if (item.id) {
                        RecentsService.recordVisit(CognitoSub, Alias, RecentEntityType.PROFILE, item.id);
                      }
                    }}
                  >
                    {item.text}
                  </a>
                );
              })}
            </div>
          );
        })}
      </div>
    </SpaceBetween>
  );
};
